import React, { useRef, useState, useEffect } from "react";
import { Box, Card, Typography, Divider, useMediaQuery, Grid, Stack, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export default function GiooPlotsDescription({
  area,
  per_sq,
  price,
  totalPrice,
  description,
  plottype,
  overallbooked,
  weeklybooked,
}) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const scrollRef = useRef(null);
  const [showArrow, setShowArrow] = useState(false);

  const formatPrice = (num) => {
    if (!num) return "N/A";
    num = Number(num);
    if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
    if (num >= 100000) return (num / 100000).toFixed(2) + " Lakhs";
    if (num >= 1000) return (num / 1000).toFixed(2) + " K";
    return num.toString();
  };

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowArrow(el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight < el.scrollHeight - 2);
  };

  useEffect(() => {
    checkScroll();
  }, [description]);

  const handleScrollDown = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: 57, behavior: "smooth" });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", flex: 1 }}>
      <Box
        sx={{
          mt: 4,
          background: "#608900",
          color: "#fff",
          px: 2,
          py: 1,
          width: "fit-content",
          borderRadius: "8px 8px 0 0",
          fontWeight: 500,
        }}
      >
        Description
      </Box>

      <Box
        sx={{
          mt: 0,
          p: isMobile ? 2 : 3,
          background: "#fff",
          borderRadius: 3,
          boxShadow: 2,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Divider />

        <Typography
          sx={{ mt: 2, wordBreak: "keep-all", overflowWrap: "break-word", whiteSpace: "normal", lineHeight: 1.6 }}
        >
          Plot Type : {plottype || ""} Sq.ft
        </Typography>

        <Typography
          sx={{ mt: 2, wordBreak: "keep-all", overflowWrap: "break-word", whiteSpace: "normal", lineHeight: 1.6 }}
        >
          Plot Area : {area || ""} Sq.ft
        </Typography>

        <Typography
          sx={{ mt: 2, wordBreak: "keep-all", overflowWrap: "break-word", whiteSpace: "normal", lineHeight: 1.6 }}
        >
          Price per Sq.ft: ₹{price || ""} (₹{formatPrice(price)})
        </Typography>

        <Typography
          sx={{ mt: 2, wordBreak: "keep-all", overflowWrap: "break-word", whiteSpace: "normal", lineHeight: 1.6 }}
        >
          Total Price: ₹{totalPrice || ""} (₹{formatPrice(totalPrice)})
        </Typography>
        <Box sx={{ mt: 0, height: "140px", position: "relative", display: "flex", flexDirection: "column" }}>
          <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "background.paper" }}>
            <strong style={{ color: "#608900", fontWeight: 400 }}>Description:</strong>
          </Box>

          <Box
            ref={scrollRef}
            onScroll={checkScroll}
            sx={{
              overflowY: "auto",
              flex: 1,
              pr: 1,
              pb: 0,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Typography
              sx={{
                whiteSpace: "pre-line",
                wordBreak: "keep-all",
                overflowWrap: "break-word",
                lineHeight: 1.8,
                letterSpacing: "0.2px",
              }}
            >
              {description || ""}
            </Typography>
          </Box>

        </Box>

        <Box
          sx={{
            height: "40px", // Fixed height to prevent layout shift
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            gap: 1,
            mt: 0.5,
            color: "#608900",
            cursor: "pointer",
            transition: "all 0.3s ease",
            visibility: showArrow ? "visible" : "hidden", // Use visibility to reserve space
            opacity: showArrow ? 1 : 0,
            "&:hover": { opacity: 0.8 },
          }}
          onClick={handleScrollDown}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
            Scroll here
          </Typography>
          <IconButton
            size="small"
            sx={{
              color: "inherit",
              p: 0,
              animation: "bounce 2s infinite",
              "@keyframes bounce": {
                "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
                "40%": { transform: "translateY(4px)" },
                "60%": { transform: "translateY(2px)" },
              },
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Box>

      </Box>

      <Grid container spacing={3} mt={6} justifyContent="space-around" alignItems="stretch">
        <Grid item xs={12} sm={6}>
          <Card
            sx={{
              p: isMobile ? 2 : 3,
              borderRadius: 4,
              height: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              boxShadow: 1,
            }}
          >
            <Stack direction="row" justifyContent="space-around" alignItems="center">
              <Box>
                <Typography fontWeight={700} fontSize={20} lineHeight={1.4}>
                  Booked Customers
                </Typography>
                <Typography color="gray" fontSize={13} lineHeight={1.4} noWrap>
                  Units booked this week
                </Typography>
              </Box>
              <Typography variant="h4" color="primary" fontWeight={500} mx={2}>
                {weeklybooked?.units_booked || 0}
              </Typography>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card
            sx={{
              p: isMobile ? 2 : 3,
              borderRadius: 4,
              height: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              boxShadow: 1,
            }}
          >
            <Stack direction="row" justifyContent="space-around" alignItems="center">
              <Box>
                <Typography fontWeight={700} fontSize={20} lineHeight={1.4}>
                  Overall Booked
                </Typography>
                <Typography color="gray" fontSize={13} lineHeight={1.4} noWrap>
                  Total booked units
                </Typography>
              </Box>
              <Typography variant="h4" color="primary" fontWeight={500} mx={2}>
                {overallbooked?.total_booked_units || 0}
              </Typography>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}